import { inject } from '@angular/core';
import { of, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { FormlyFieldConfig } from '@ngx-formly/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

import { CompanyService } from '../../../api/services/company.service';
import { RegionService } from '../../../api/services/region.service';
import { EducationLevelService } from '../../../api/services/education-level.service';
import { JobPostingService } from '../../../api/services/job-posting.service';

import type { JobPostingDocument } from '../../../api/models/job-posting-document';
import { JobPostingHours } from '../../../api/models/job-posting-hours';
import { JobPostingStatus } from '../../../api/models/job-posting-status';
import { JobPostingType } from '../../../api/models/job-posting-type';
import { SortType } from '../../../api/models/sort-type';

import { alertMessages } from '../../services/alert.service';
import { targetGroupOptions } from '../../services/job.service';
import type { JobPostingInput, JobPostingModel } from './job-posting';

const STATUS_OPTIONS = [
  { value: JobPostingStatus.Private, label: _('Private') },
  { value: JobPostingStatus.Public, label: _('Public') },
  { value: JobPostingStatus.Closed, label: _('Closed') },
];

// Company mode (legacy JobService#getTypes) — excludes Assignment.
// Admin mode (legacy JobService#getTypesAll) — includes Assignment.
const TYPE_OPTIONS_BASE = [
  { value: JobPostingType.Job, label: _('Job') },
  { value: JobPostingType.Internship, label: _('Working place / Internship') },
  { value: JobPostingType.Traineeship, label: _('Traineeship') },
  { value: JobPostingType.ApprenticeshipPosition, label: _('ApprenticeshipPosition') },
  { value: JobPostingType.Sidejob, label: _('Side job') },
];
const TYPE_OPTIONS_ADMIN = [
  ...TYPE_OPTIONS_BASE,
  { value: JobPostingType.Assignment, label: _('Assignment') },
];

const HOURS_OPTIONS = [
  { value: JobPostingHours.Parttime, label: _('Parttime') },
  { value: JobPostingHours.Fulltime, label: _('Fulltime') },
];

export function buildJobPostingFields(
  input: JobPostingInput,
  model: JobPostingModel,
): FormlyFieldConfig[] {
  return [
    {
      type: 'mat-tabs',
      fieldGroup: [
        // ───────────────────────────────────────────── Tab: General
        {
          props: { label: _('General'), tabId: 'general' },
          fieldGroupClassName: 'flex flex-col gap-4',
          fieldGroup: [
            {
              key: 'regions',
              type: 'region-checkboxes',
              defaultValue: !input.isAdminMode ? (input.profile?.regions ?? []) : undefined,
              props: {
                label: _('Regions'),
                required: true,
                mode: input.isAdminMode ? 'admin' : 'company',
                ...(input.isAdminMode
                  ? { loadOptions: () => inject(RegionService).getRegions() }
                  : { companyRegions: input.profile?.regions ?? [] }),
              },
            },
            {
              key: 'jobPostingTargetGroups',
              type: 'tag-selector',
              props: { label: _('Target groups'), options: targetGroupOptions },
            },
            {
              key: 'companyId',
              type: 'mat-select',
              hide: !input.isAdminMode,
              defaultValue: !input.isAdminMode ? input.profile?.id : undefined,
              props: {
                label: _('Company'),
                required: input.isAdminMode,
                loadOptions: () =>
                  inject(CompanyService)
                    .getCompanies({
                      SortField: 'name',
                      SortType: SortType.Ascending,
                      PageNumber: 1,
                      PageSize: 1200,
                    })
                    .pipe(
                      map((r) =>
                        (r?.data ?? []).flatMap((c) =>
                          c.id && c.name ? [{ value: c.id, label: c.name }] : [],
                        ),
                      ),
                    ),
              },
            },
            {
              key: 'trademarkId',
              type: 'mat-select',
              props: {
                label: _('Trademark'),
                hideWhenEmpty: true,
                dependsOn: ['companyId'],
                loadOptions: () => {
                  const companyId =
                    model?.companyId ?? (!input.isAdminMode ? input.profile?.id : null);
                  if (!companyId) return of([]);
                  return inject(CompanyService)
                    .getCompanyTrademarks({ companyId })
                    .pipe(
                      map((list) =>
                        (list ?? []).flatMap((t) =>
                          t.id ? [{ value: t.id, label: t.name }] : [],
                        ),
                      ),
                    );
                },
              },
            },
            {
              key: 'status',
              type: 'mat-select',
              props: { label: _('Status'), required: true, options: STATUS_OPTIONS },
            },
            {
              key: 'type',
              type: 'mat-select',
              props: {
                label: _('Type'),
                required: true,
                options: input.isAdminMode ? TYPE_OPTIONS_ADMIN : TYPE_OPTIONS_BASE,
              },
            },
            { key: 'title', type: 'input-translatable', props: { label: _('Title'), requireOneLocale: true, fieldBaseName: 'title' } },
            { key: 'location', type: 'input-translatable', props: { label: _('Location'), fieldBaseName: 'location' } },
            {
              key: 'description',
              type: 'ckeditor-translatable',
              props: { label: _('Description'), requireOneLocale: true, fieldBaseName: 'description' },
            },
            { key: 'requirements', type: 'ckeditor-translatable', props: { label: _('Requirements'), fieldBaseName: 'requirements' } },
            {
              key: 'requiredSkills',
              type: 'skills-input',
              props: {
                label: _('Required skills'),
                descriptionField: 'description',
                placeholder: _('Add a required skill'),
              },
            },
            {
              key: 'termsOfEmployment',
              type: 'ckeditor-translatable',
              props: { label: _('Terms of employment'), fieldBaseName: 'termsOfEmployment' },
            },
            {
              key: 'additionalInfo',
              type: 'ckeditor-translatable',
              props: { label: _('Additional information'), fieldBaseName: 'additionalInfo' },
            },
            {
              key: 'externalReferenceUri',
              type: 'mat-input',
              props: { label: _('External link'), inputType: 'url' },
            },
          ],
        },

        // ─────────────────────────────────────────────── Tab: Match
        {
          props: { label: _('Match'), tabId: 'match' },
          fieldGroupClassName: 'flex flex-col gap-6',
          fieldGroup: [
            {
              key: 'matchcriterion',
              type: 'match-criterion',
              props: { entityId: input.entity?.id },
            },
          ],
        },

        // ───────────────────────────────────────── Tab: Requirements
        {
          props: { label: _('Requirements'), tabId: 'requirements' },
          fieldGroupClassName: 'flex flex-col gap-4',
          fieldGroup: [
            {
              key: 'immediatelyAvailable',
              type: 'mat-checkbox',
              props: { label: _('Immediately available') },
            },
            {
              key: 'startDate',
              type: 'mat-datepicker',
              props: { label: _('Start date') },
              expressions: { 'props.disabled': 'model.immediatelyAvailable === true' },
            },
            { key: 'deadline', type: 'mat-datepicker', props: { label: _('Deadline') } },
            {
              key: 'hours',
              type: 'mat-radio',
              props: { label: _('Hours'), required: true, options: HOURS_OPTIONS, direction: 'row' },
            },
            {
              key: 'hoursPerWeek',
              type: 'mat-number',
              props: { label: _('Hours per week'), min: 1, max: 40, suffix: _('hours') },
              expressions: { hide: `model.hours !== "${JobPostingHours.Parttime}"` },
            },
            {
              key: 'isExperienceDesired',
              type: 'mat-checkbox',
              props: { label: _('Experience desired') },
            },
            {
              key: 'directApplyLink',
              type: 'mat-input',
              props: { label: _('Direct apply link'), inputType: 'url' },
            },
            {
              key: 'attachment',
              type: 'file-upload-with-history',
              props: {
                label: _('Attachment'),
                accept: '.pdf,.doc,.docx',
                existingDocuments: (input.entity?.documents ?? []).map((d) => {
                  const raw = d as JobPostingDocument & { name?: string; url?: string };
                  return {
                    id: raw.id,
                    name: raw.name ?? raw.filename,
                    url: raw.url ?? raw.uri,
                  };
                }),
                deleteDoc: (id: string): Observable<unknown> => {
                  if (!model.id) return of(null);
                  return inject(JobPostingService).deleteJobPostingDocument({
                    jobPostingId: model.id,
                    id,
                  });
                },
                confirmDelete: {
                  title: _('Delete attachment'),
                  message: _('Are you sure you want to delete this attachment?'),
                },
                deleteSuccessMessage: alertMessages.successDataRemoved,
              },
            },
          ],
        },

        // ─────────────────────────────────────────── Tab: Education
        {
          props: { label: _('Education'), tabId: 'education' },
          fieldGroupClassName: 'flex flex-col gap-4',
          fieldGroup: [
            {
              key: 'educationLevels',
              type: 'education-level-tree',
              props: {
                label: _('Education levels'),
                excludedRegions: ['EU'],
                loadOptions: () =>
                  inject(EducationLevelService)
                    .getEducationLevels({ PageNumber: 1, PageSize: 500 })
                    .pipe(map((r) => r?.data ?? [])),
              },
            },
          ],
        },

        // ───────────────────────────────────────────── Tab: Contact
        {
          props: { label: _('Contact'), tabId: 'contact' },
          fieldGroupClassName: 'flex flex-col gap-4',
          fieldGroup: [
            {
              key: 'address',
              fieldGroupClassName: 'grid grid-cols-1 sm:grid-cols-6 gap-3',
              defaultValue:
                !input.entity && !input.isAdminMode
                  ? (input.profile?.visitAddress ?? {})
                  : undefined,
              fieldGroup: [
                { key: 'street', type: 'mat-input', className: 'sm:col-span-3',
                  props: { label: _('Street'), required: true } },
                { key: 'number', type: 'mat-input', className: 'sm:col-span-1',
                  props: { label: _('House number'), required: true } },
                { key: 'numberSuffix', type: 'mat-input', className: 'sm:col-span-2',
                  props: { label: _('Suffix') } },
                { key: 'postalCode', type: 'mat-input', className: 'sm:col-span-2',
                  props: { label: _('Postal code'), required: true } },
                { key: 'city', type: 'mat-input', className: 'sm:col-span-2',
                  props: { label: _('City'), required: true } },
                { key: 'country', type: 'mat-input', className: 'sm:col-span-2',
                  props: { label: _('Country'), required: true } },
              ],
            },
            {
              key: 'contactPersonId',
              type: 'mat-select',
              props: {
                label: _('Contact person'),
                dependsOn: ['companyId'],
                loadOptions: () => {
                  const companyId =
                    model?.companyId ?? (!input.isAdminMode ? input.profile?.id : null);
                  if (!companyId) return of([]);
                  return inject(CompanyService)
                    .getCompanyAccounts({ companyId })
                    .pipe(
                      map((list) =>
                        (list ?? []).flatMap((ca) => {
                          const id = ca.account?.id;
                          if (!id) return [];
                          const name = [ca.account?.firstName, ca.account?.lastName]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          return [{ value: id, label: name || id }];
                        }),
                      ),
                    );
                },
              },
            },
          ],
        },
      ],
    },
  ];
}
